import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Github, ExternalLink, Target, Lightbulb, Wrench, Trophy, User, Tag, Calendar, Zap } from "lucide-react";
import { StatusBadge } from "./shared";

const BLOCKS = [
  { key: "problem", label: "The Problem", icon: Target, accent: "text-red-400", bg: "bg-red-400/10", line: "border-red-400/20", bar: "bg-red-400/70" },
  { key: "solution", label: "The Solution", icon: Lightbulb, accent: "text-amber-400", bg: "bg-amber-400/10", line: "border-amber-400/20", bar: "bg-amber-400/70" },
  { key: "challenges", label: "Challenges", icon: Wrench, accent: "text-cyan-400", bg: "bg-cyan-400/10", line: "border-cyan-400/20", bar: "bg-cyan-400/70" },
  { key: "outcome", label: "Outcome", icon: Trophy, accent: "text-emerald-400", bg: "bg-emerald-400/10", line: "border-emerald-400/20", bar: "bg-emerald-400/70" },
];

const highlightNumbers = (text) =>
  text.split(/(\d[\d,.]*[%+x×]?)/g).map((part, i) =>
    /^\d/.test(part) ? (
      <span key={i} className="font-semibold text-amber-300">{part}</span>
    ) : (
      part
    )
  );

export const ProjectDetailModal = ({ project, onClose }) => {
  useEffect(() => {
    if (!project) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
  }, [project, onClose]);

  return (
    <AnimatePresence>
      {project && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/75 backdrop-blur-md p-3 sm:p-6"
          onClick={onClose}
          data-testid="project-detail-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 44, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 44, scale: 0.96 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`${project.title} details`}
            className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto lg:overflow-hidden rounded-3xl border border-white/10 bg-[#0A0D15] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.9)] lg:grid lg:grid-cols-[400px_minmax(0,1fr)]"
            data-testid="project-detail-panel"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-20 rounded-full border border-white/15 bg-black/50 backdrop-blur p-2 text-slate-300 hover:text-white hover:border-white/30 transition-colors"
              aria-label="Close project details"
              data-testid="project-modal-close"
            >
              <X size={18} />
            </button>

            <div className="relative h-60 sm:h-72 lg:h-auto overflow-hidden">
              <img src={project.image} alt={`${project.title} mockup`} className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D15] via-[#0A0D15]/50 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-3">
                  <StatusBadge status={project.status} />
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/40 backdrop-blur px-2.5 py-0.5 font-code text-[11px] text-slate-300">
                    <Calendar size={10} /> {project.year}
                  </span>
                </div>
                <p className="font-code text-[11px] uppercase tracking-[0.2em] text-amber-400/90">{project.type}</p>
                <h3 className="mt-1.5 font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">{project.title}</h3>
                <div className="mt-3 space-y-1.5 text-xs text-slate-400">
                  {project.role && (
                    <p className="flex items-center gap-2"><User size={12} className="text-slate-500" /> {project.role}</p>
                  )}
                  {project.client && (
                    <p className="flex items-center gap-2"><Tag size={12} className="text-slate-500" /> {project.client}</p>
                  )}
                </div>
                <div className="mt-5 flex flex-wrap gap-2.5">
                  {project.playStore && (
                    <a
                      href={project.playStore}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-2 text-xs font-semibold text-[#07090E] hover:bg-amber-300 transition-colors"
                      data-testid="project-modal-playstore"
                    >
                      <ExternalLink size={13} /> View on Play Store
                    </a>
                  )}
                  {project.liveUrl && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-2 text-xs font-semibold text-[#07090E] hover:bg-amber-300 transition-colors"
                      data-testid="project-modal-live"
                    >
                      <ExternalLink size={13} /> Live Project
                    </a>
                  )}
                  {project.github && (
                    <a
                      href={project.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/40 backdrop-blur px-4 py-2 text-xs font-semibold text-slate-200 hover:border-white/40 transition-colors"
                      data-testid="project-modal-github"
                    >
                      <Github size={13} /> View Code
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 lg:max-h-[90vh] lg:overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((t) => (
                  <span key={t} className="rounded-md border border-white/[0.08] bg-white/[0.03] px-2.5 py-1 text-[11px] text-slate-400 transition-colors hover:border-amber-400/40 hover:text-amber-300">
                    {t}
                  </span>
                ))}
              </div>

              <div className="mt-7 space-y-4">
                {BLOCKS.map((b, i) =>
                  project[b.key] ? (
                    <motion.div
                      key={b.key}
                      initial={{ opacity: 0, x: 18 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.08, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className={`relative overflow-hidden rounded-2xl border ${b.line} bg-white/[0.02] p-5`}
                      data-testid={`project-block-${b.key}`}
                    >
                      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${b.bar}`} />
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <span className={`rounded-lg border ${b.line} ${b.bg} p-1.5 ${b.accent}`}>
                          <b.icon size={14} />
                        </span>
                        <span className="font-code text-[10px] uppercase tracking-[0.25em] text-slate-500">
                          {String(i + 1).padStart(2, "0")} — {b.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-300 leading-relaxed">{project[b.key]}</p>
                    </motion.div>
                  ) : null
                )}
              </div>

              {project.achievements?.length > 0 && (
                <div className="mt-7">
                  <p className="mb-3 flex items-center gap-2 font-code text-[10px] uppercase tracking-[0.25em] text-emerald-400">
                    <Zap size={12} /> Impact
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {project.achievements.map((a, i) => (
                      <motion.div
                        key={a}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 + i * 0.07, duration: 0.4 }}
                        className="rounded-xl border border-emerald-400/15 bg-emerald-400/[0.05] px-4 py-3 text-sm text-slate-200 leading-snug"
                      >
                        {highlightNumbers(a)}
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
